import { useState, useEffect } from "react";
import { Check } from "lucide-react";

// Match Found Loading Screen
export default function LandingScreen({ onComplete }) {
  const [progress, setProgress] = useState(0);
  const [playersAccepted, setPlayersAccepted] = useState(0);
  const [phase, setPhase] = useState("searching"); // searching, found, accepting, entering

  useEffect(() => {
    // Phase 1: Searching (0.5s)
    const searchTimer = setTimeout(() => {
      setPhase("found");
    }, 200);

    // Phase 2: Found - start accepting (0.7s)
    const foundTimer = setTimeout(() => {
      setPhase("accepting");
    }, 300);

    // Phase 3: Players accept one by one
    const acceptIntervals = [];
    for (let i = 1; i <= 10; i++) {
      acceptIntervals.push(
        setTimeout(
          () => {
            setPlayersAccepted(i);
          },
          500 + i * 100,
        ),
      );
    }

    // Phase 4: Entering (after all accept)
    const enterTimer = setTimeout(() => {
      setPhase("entering");
    }, 1700);

    // Complete and transition
    const completeTimer = setTimeout(() => {
      onComplete();
    }, 2000);

    return () => {
      clearTimeout(searchTimer);
      clearTimeout(foundTimer);
      acceptIntervals.forEach(clearTimeout);
      clearTimeout(enterTimer);
      clearTimeout(completeTimer);
    };
  }, [onComplete]);

  // Progress bar animation
  useEffect(() => {
    if (phase === "accepting" || phase === "entering") {
      const interval = setInterval(() => {
        setProgress((prev) => Math.min(prev + 2, 100));
      }, 30);
      return () => clearInterval(interval);
    }
  }, [phase]);

  return (
    <div
      className="fixed inset-0 z-[100] bg-[#010A13] flex items-center justify-center"
      data-testid="match-found-screen"
    >
      {/* Background - clean, no pulse effects */}
      <div className="absolute inset-0 bg-[#010A13]" />

      <div className="relative z-10 text-center">
        {/* Match Found Text */}
        <div
          className={`transition-all duration-300 ${phase === "searching" ? "opacity-100" : "opacity-0 -translate-y-4"}`}
        >
          <p className="text-[#A09B8C] text-lg uppercase tracking-[0.3em]">
            Searching...
          </p>
        </div>

        <div
          className={`absolute inset-0 flex flex-col items-center justify-center transition-all duration-500 ${phase !== "searching" ? "opacity-100" : "opacity-0 translate-y-4"}`}
        >
          {/* Main Circle Timer */}
          <div className="relative w-56 h-56 mb-8">
            {/* Outer ring */}
            <svg className="w-full h-full -rotate-90" viewBox="0 0 100 100">
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#3C3C41"
                strokeWidth="2"
              />
              <circle
                cx="50"
                cy="50"
                r="45"
                fill="none"
                stroke="#0AC8B9"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={`${progress * 2.83} 283`}
                className="transition-all duration-100 drop-shadow-[0_0_10px_rgba(10,200,185,0.6)]"
              />
            </svg>

            {/* Inner content */}
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              {phase === "entering" ? (
                <Check className="w-16 h-16 text-[#0AC8B9] animate-pulse" />
              ) : (
                <>
                  <span className="text-[#C89B3C] font-['Marcellus'] text-3xl uppercase tracking-wider">
                    Summoner
                  </span>
                  <span className="text-[#F0E6D2] font-['Marcellus'] text-xl uppercase tracking-wider">
                    Found
                  </span>
                </>
              )}
            </div>
          </div>

          {/* Player Accept Indicators */}
          <div className="flex gap-2 mb-6">
            {[...Array(10)].map((_, i) => (
              <div
                key={i}
                className={`w-6 h-6 border transition-all duration-300 flex items-center justify-center ${
                  i < playersAccepted
                    ? "border-[#0AC8B9] bg-[#0AC8B9]/20"
                    : "border-[#3C3C41] bg-transparent"
                }`}
              >
                {i < playersAccepted && (
                  <Check className="w-4 h-4 text-[#0AC8B9]" />
                )}
              </div>
            ))}
          </div>

          {/* Status Text */}
          <p
            className={`text-lg uppercase tracking-[0.2em] transition-all duration-300 ${
              phase === "entering" ? "text-[#0AC8B9]" : "text-[#A09B8C]"
            }`}
          >
            {phase === "entering"
              ? "Entering Profile..."
              : `${playersAccepted}/10 Accepted`}
          </p>
        </div>
      </div>

      {/* Bottom decorative line */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-[#3C3C41]">
        <div
          className="h-full bg-[#0AC8B9] transition-all duration-100 shadow-[0_0_20px_rgba(10,200,185,0.6)]"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
